import { useState } from 'react';
import type { TapEffectSpec, TapEffectVariant, TapSprite } from '../../../themes/types';
import { TAP_SPRITE_EMOJI } from '../../../effects/tapSprites';
import { EmojiPicker } from '../../profiles/EmojiPicker';
import { useVariantKeys } from './useVariantKeys';

interface TapEffectSectionProps {
  readonly value: TapEffectSpec;
  readonly onChange: (value: TapEffectSpec) => void;
}

const KINDS: TapEffectVariant['kind'][] = ['fly-to-counter', 'sparkle-burst', 'shrink-fade'];
const KIND_LABEL: Record<TapEffectVariant['kind'], string> = {
  'fly-to-counter': 'Lento tähtiin',
  'sparkle-burst': 'Kipinäryöppy',
  'shrink-fade': 'Kutistuu',
};
const SPARKLE_SPRITES: TapSprite[] = ['star', 'heart', 'sparkle', 'confetti', 'coin'];
const MAX_FLYING_EMOJIS = 5;

export function TapEffectSection({ value, onChange }: TapEffectSectionProps) {
  const keyFor = useVariantKeys<TapEffectVariant>();
  const [pickerFor, setPickerFor] = useState<number | null>(null);

  const setVariant = (idx: number, variant: TapEffectVariant) => {
    const next = value.variants.slice();
    next[idx] = variant;
    onChange({ ...value, variants: next });
  };
  const addVariant = () =>
    onChange({ ...value, variants: [...value.variants, { kind: 'fly-to-counter' }] });
  const removeVariant = (idx: number) =>
    onChange({ ...value, variants: value.variants.filter((_, i) => i !== idx) });

  const appendEmoji = (idx: number, emoji: string) => {
    const variant = value.variants[idx];
    if (variant?.kind !== 'fly-to-counter') return;
    const current = variant.emojis ?? [];
    if (current.length >= MAX_FLYING_EMOJIS) return;
    setVariant(idx, { ...variant, emojis: [...current, emoji] });
  };
  const removeEmojiAt = (variantIdx: number, emojiIdx: number) => {
    const variant = value.variants[variantIdx];
    if (variant?.kind !== 'fly-to-counter') return;
    const next = (variant.emojis ?? []).filter((_, i) => i !== emojiIdx);
    setVariant(variantIdx, { ...variant, emojis: next });
  };

  return (
    <fieldset className="theme-editor__section">
      <legend>Kun tehtävä täytetään</legend>
      {value.variants.map((v, i) => (
        <div key={keyFor(v)} className="theme-editor__variant-row">
          <select
            value={v.kind}
            onChange={(e) =>
              setVariant(i, {
                kind: e.target.value as TapEffectVariant['kind'],
              } as TapEffectVariant)
            }
          >
            {KINDS.map((k) => (
              <option key={k} value={k}>
                {KIND_LABEL[k]}
              </option>
            ))}
          </select>
          {v.kind === 'fly-to-counter' ? (
            <FlyingEmojiChips
              emojis={v.emojis ?? []}
              onAdd={() => setPickerFor(i)}
              onRemove={(emojiIdx) => removeEmojiAt(i, emojiIdx)}
            />
          ) : null}
          {v.kind === 'sparkle-burst' ? (
            <div className="theme-editor__chips">
              {SPARKLE_SPRITES.map((s) => (
                <button
                  key={s}
                  type="button"
                  className={v.sprite === s ? 'active' : ''}
                  onClick={() => setVariant(i, { ...v, sprite: s })}
                >
                  {TAP_SPRITE_EMOJI[s]}
                </button>
              ))}
            </div>
          ) : null}
          {value.variants.length > 1 ? (
            <button type="button" onClick={() => removeVariant(i)} aria-label="Poista">
              ✕
            </button>
          ) : null}
        </div>
      ))}
      <button type="button" onClick={addVariant}>
        Lisää variantti
      </button>
      {value.variants.length > 1 ? (
        <label>
          <span>Valintatapa</span>
          <select
            value={value.pickMode}
            onChange={(e) =>
              onChange({ ...value, pickMode: e.target.value as 'sequential' | 'random' })
            }
          >
            <option value="sequential">Vuoro</option>
            <option value="random">Arpa</option>
          </select>
        </label>
      ) : null}
      {pickerFor !== null && (
        <EmojiPicker
          onPick={(emoji) => {
            appendEmoji(pickerFor, emoji);
            setPickerFor(null);
          }}
          onCancel={() => setPickerFor(null)}
        />
      )}
    </fieldset>
  );
}

interface FlyingEmojiChipsProps {
  readonly emojis: readonly string[];
  readonly onAdd: () => void;
  readonly onRemove: (idx: number) => void;
}

function FlyingEmojiChips({ emojis, onAdd, onRemove }: FlyingEmojiChipsProps) {
  return (
    <div className="theme-editor__chips">
      {emojis.map((emoji, idx) => (
        <button
          key={`${emoji}-${idx}`}
          type="button"
          className="active"
          onClick={() => onRemove(idx)}
          aria-label={`Poista lentävä ${emoji}`}
        >
          {emoji}
        </button>
      ))}
      {emojis.length < MAX_FLYING_EMOJIS ? (
        <button
          type="button"
          onClick={onAdd}
          aria-label="Lisää lentävä emoji"
          title={
            emojis.length === 0 ? 'Käytä palkintoemojia (oletus) tai lisää omat' : 'Lisää emoji'
          }
        >
          +
        </button>
      ) : null}
    </div>
  );
}
